import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { useAuth } from '../../contexts/AuthContext';
import useClientFallEvents from '../../hooks/client/useClientFallEvents';
import { scopeEventsForClient } from '../../utils/eventFilters';
import { buildClipUrl } from '../../utils/media';
import FallEventDrawer from '../admin/components/fall-events/FallEventDrawer';
import LivePreviewDialog from '../admin/components/monitoring/LivePreviewDialog';
import { getCameraList, sendCameraHeartbeat } from '../../services/cameraService';
import config from '../../config/app.config';
import ClientOverviewTab from './components/ClientOverviewTab';
import ClientCameraTab from './components/ClientCameraTab';

const ClientDashboardPage = () => {
  const { user } = useAuth();
  const {
    events: rawEvents,
    loading,
    error,
    saveEvent,
  } = useClientFallEvents();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const [cameras, setCameras] = useState([]);
  const [cameraLoading, setCameraLoading] = useState(true);
  const [cameraError, setCameraError] = useState('');
  const [previewCamera, setPreviewCamera] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pendingPreviewId, setPendingPreviewId] = useState(null);
  const [tab, setTab] = useState(0);

  const apiBase = useMemo(() => config.api.baseURL.replace(/\/$/, ''), []);

  const scopedEvents = useMemo(() => scopeEventsForClient(rawEvents, user), [rawEvents, user]);
  const eventsForClient = useMemo(() => {
    const source = scopedEvents.events.length ? scopedEvents.events : rawEvents;
    if (!Array.isArray(source)) {
      return [];
    }
    return source.slice(0, 10);
  }, [rawEvents, scopedEvents.events]);

  const loadCameras = useCallback(async () => {
    setCameraLoading(true);
    setCameraError('');
    try {
      const data = await getCameraList();
      setCameras(Array.isArray(data) ? data : []);
    } catch (err) {
      setCameraError('Unable to load your cameras right now.');
    } finally {
      setCameraLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCameras();
  }, [loadCameras]);

  const totalEvents = eventsForClient.length;
  const eventsThisMonth = useMemo(() => {
    const now = new Date();
    return eventsForClient.filter((event) => {
      const ts = new Date(event.timestamp || event.detected_at || 0);
      return ts.getMonth() === now.getMonth() && ts.getFullYear() === now.getFullYear();
    }).length;
  }, [eventsForClient]);

  const unresolvedEvents = useMemo(
    () => eventsForClient.filter((event) => !['CLOSED', 'ADMIN_REVIEWED'].includes(event.status?.toUpperCase?.() || '')).length,
    [eventsForClient]
  );

  const lastUpdated = eventsForClient[0]
    ? new Date(eventsForClient[0].timestamp || eventsForClient[0].detected_at || Date.now())
    : null;

  const overviewStats = useMemo(
    () => [
      {
        label: 'Incidents recorded',
        value: totalEvents,
        caption: 'Total falls linked to your account',
        icon: <WarningAmberIcon />,
      },
      {
        label: 'This month',
        value: eventsThisMonth,
        caption: 'Falls detected in the current month',
        icon: <AssessmentIcon />,
      },
      {
        label: 'Open follow-ups',
        value: unresolvedEvents,
        caption: 'Incidents awaiting review or closure',
        icon: <AccessTimeIcon />,
      },
    ],
    [eventsThisMonth, totalEvents, unresolvedEvents],
  );

  const recentEvents = useMemo(() => eventsForClient.slice(0, 5), [eventsForClient]);

  const overviewFooter = lastUpdated
    ? `Last updated ${lastUpdated.toLocaleString()}`
    : 'Last updated recently.';

  const handleOpenEvent = (event) => {
    if (!event) return;
    setSelectedEvent(event);
    setDrawerOpen(true);
  };

  const handleBrowseHistory = () => navigate('/fall-logs');
  const handleContactSupport = () => navigate('/contact');
  const handleTabChange = (_event, value) => setTab(value);

  const handleSaveEvent = async (eventId, payload) => {
    const updatedEvent = await saveEvent(eventId, payload);
    setSelectedEvent(updatedEvent);
    setDrawerOpen(false);
  };

  const handleOpenPreview = async (camera) => {
    if (!camera) return;
    setCameraError('');
    setPendingPreviewId(camera.id);
    setPreviewLoading(true);
    try {
      await sendCameraHeartbeat(camera.id, { keepaliveSeconds: 120, source: 'client_preview' });
      setPreviewCamera(camera);
    } catch (err) {
      setCameraError('Unable to open the live preview right now. Please try again.');
    } finally {
      setPreviewLoading(false);
      setPendingPreviewId(null);
    }
  };

  const handleClosePreview = () => setPreviewCamera(null);

  const formatCameraHeartbeat = useCallback((value) => {
    if (!value) {
      return 'No status yet';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Unknown';
    }
    return date.toLocaleString();
  }, []);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Welcome back, {user?.first_name || user?.username}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Here's a quick view of your recent incidents, recordings, and health checks.
          </Typography>
          {scopedEvents.fallback && (
            <Alert severity="info" sx={{ mt: 2 }}>
              We couldn't link any incidents directly to your profile yet, so we're showing the most recent falls
              recorded across the organisation.
            </Alert>
          )}
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={handleTabChange} variant="scrollable" allowScrollButtonsMobile>
            <Tab label="Overview" />
            <Tab label="Cameras" />
          </Tabs>
        </Box>

        {tab === 0 && (
          <ClientOverviewTab
            stats={overviewStats}
            recentEvents={recentEvents}
            onSelectEvent={handleOpenEvent}
            onBrowseHistory={handleBrowseHistory}
            onContactSupport={handleContactSupport}
            footerCaption={overviewFooter}
          />
        )}

        {tab === 1 && (
          <ClientCameraTab
            cameras={cameras}
            loading={cameraLoading}
            error={cameraError}
            onRefresh={loadCameras}
            formatHeartbeat={formatCameraHeartbeat}
            onPreview={handleOpenPreview}
            previewLoading={previewLoading}
            pendingPreviewId={pendingPreviewId}
          />
        )}
      </Stack>

      <FallEventDrawer
        open={drawerOpen}
        event={selectedEvent}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveEvent}
        buildClipUrl={buildClipUrl}
      />

      <LivePreviewDialog open={Boolean(previewCamera)} camera={previewCamera} apiBase={apiBase} onClose={handleClosePreview} />
    </Container>
  );
};

export default ClientDashboardPage;

